import { API } from '@providers/ApiInterceptorProvider/useApiInterceptor';
import { cleanAntdFormPayload } from './cleanAntdFormPayload';
import {
  AffordabilityRunRecord,
  ApplicationToSolicitor,
  BtlAffordabilityRunResponse,
  BtlApplication,
  BtlCaseSubmissionResult,
  Borrower,
  BtlCompany,
  CreateApplicationToSolicitorDto,
  CreateCaseDto,
  CreateCompanyDto,
  CreateHouseholdDto,
  CreateIndividualIncomesDto,
  Household,
  Individual,
  IndividualCreateDto,
  IndividualIncome,
  LinkApplicationToSolicitorDto,
  SolicitorFirmWithSolicitors,
  SubmitDipDto,
  SubmitFullApplicationDto,
  UpdateBankAccountDto,
  UpdateCaseDto,
  UpdateCompanyDto,
  UpdateIndividualIncomeDto,
} from './types';
import { Conveyancing } from './conveyancingTypes';

export const btlApplicationFormApi = {
  getApplicationInProgress: async (applicationId: string) => {
    try {
      const response = await API.get<Record<string, string>>(`/btl-applications/${applicationId}`);
      return response.data;
    } catch (err: any) {
      throw new Error(err.response?.data?.message ?? err);
    }
  },
  getMissingFields: async (applicationId: string) => {
    try {
      const response = await API.get<any>(`/btl-applications/${applicationId}/missing-fields`);
      return response.data;
    } catch (err: any) {
      throw new Error(err.response?.data?.message ?? err);
    }
  },
  getBorrower: async (caseId: string) => {
    try {
      const response = await API.get<Borrower>(`/btl-applications/${caseId}/borrowers`);
      return response.data;
    } catch (err: any) {
      throw new Error(err.response?.data?.message ?? err);
    }
  },
  createIndividual: async (applicationId: string, borrowerId: string, body: IndividualCreateDto) => {
    try {
      const response = await API.post<Individual>(
        `/btl-applications/${applicationId}/borrowers/${borrowerId}/individuals`,
        cleanAntdFormPayload(body),
      );
      return response.data;
    } catch (err: any) {
      throw new Error(err.response?.data?.message ?? err);
    }
  },
  editIndividual: async (
    applicationId: string,
    borrowerId: string,
    individualId: string,
    body: IndividualCreateDto,
  ) => {
    try {
      const response = await API.put<Individual>(
        `/btl-applications/${applicationId}/borrowers/${borrowerId}/individuals/${individualId}`,
        cleanAntdFormPayload(body),
      );
      return response.data;
    } catch (err: any) {
      throw new Error(err.response?.data?.message ?? err);
    }
  },
  deleteIndividual: async (applicationId: string, borrowerId: string, individualId: string) => {
    try {
      const response = await API.delete<void>(
        `/btl-applications/${applicationId}/borrowers/${borrowerId}/individuals/${individualId}`,
      );
      return response.data;
    } catch (err: any) {
      throw new Error(err.response?.data?.message ?? err);
    }
  },
  linkIndividualToHousehold: async (
    applicationId: string,
    borrowerId: string,
    individualId: string,
    body: {
      householdId: string;
    },
  ) => {
    try {
      const response = await API.post<void>(
        `/btl-applications/${applicationId}/borrowers/${borrowerId}/individuals/${individualId}/link-household`,
        body,
      );
      return response.data;
    } catch (err: any) {
      throw new Error(err.response?.data?.message ?? err);
    }
  },
  unlinkIndividualFromHousehold: async (applicationId: string, borrowerId: string, individualId: string) => {
    try {
      const response = await API.post<void>(
        `/btl-applications/${applicationId}/borrowers/${borrowerId}/individuals/${individualId}/unlink-household`,
      );
      return response.data;
    } catch (err: any) {
      throw new Error(err.response?.data?.message ?? err);
    }
  },
  getIndividualIncomes: async (applicationId: string, borrowerId: string, individualId: string) => {
    try {
      const response = await API.get<CreateIndividualIncomesDto>(
        `/btl-applications/${applicationId}/borrowers/${borrowerId}/individuals/${individualId}/incomes`,
      );
      return response.data;
    } catch (err: any) {
      throw new Error(err.response?.data?.message ?? err);
    }
  },
  createIndividualIncome: async (
    applicationId: string,
    borrowerId: string,
    individualId: string,
    body?: UpdateIndividualIncomeDto,
  ) => {
    try {
      const response = await API.put<IndividualIncome>(
        `/btl-applications/${applicationId}/borrowers/${borrowerId}/individuals/${individualId}/incomes`,
        body,
      );
      return response.data;
    } catch (err: any) {
      throw new Error(err.response?.data?.message ?? err);
    }
  },
  createCompany: async (applicationId: string, body: CreateCompanyDto) => {
    type companyId = string;
    try {
      const response = await API.post<companyId>(`/btl-applications/${applicationId}/companies`, body);
      return response.data;
    } catch (err: any) {
      throw new Error(err.response?.data?.message ?? err);
    }
  },
  editCompany: async (applicationId: string, companyId: string, body: UpdateCompanyDto) => {
    try {
      const response = await API.put<BtlCompany>(`/btl-applications/${applicationId}/companies/${companyId}`, body);
      return response.data;
    } catch (err: any) {
      throw new Error(err.response?.data?.message ?? err);
    }
  },
  deleteCompany: async (applicationId: string, companyId: string) => {
    try {
      const response = await API.delete<void>(`/btl-applications/${applicationId}/companies/${companyId}`);
      return response.data;
    } catch (err: any) {
      throw new Error(err.response?.data?.message ?? err);
    }
  },
  createApplication: async (body: CreateCaseDto) => {
    type applicationId = string;
    try {
      const response = await API.put<applicationId>(`/btl-applications`, body);
      return response.data;
    } catch (err: any) {
      throw new Error(err.response?.data?.message ?? err);
    }
  },
  updateFinanceAndProperty: async (applicationId: string, body: UpdateCaseDto) => {
    try {
      const response = await API.patch<BtlApplication>(
        `/btl-applications/${applicationId}/finance-and-property`,
        cleanAntdFormPayload(body),
      );
      return response.data;
    } catch (err: any) {
      throw new Error(err.response?.data?.message ?? err);
    }
  },
  submitDipRequest: async (applicationId: string, body: SubmitDipDto) => {
    try {
      const response = await API.post<BtlCaseSubmissionResult>(`/btl-applications/${applicationId}/submit`, body);
      return response.data;
    } catch (err: any) {
      throw new Error(err.response?.data?.message ?? err);
    }
  },
  getAffordabilityRuns: async (applicationId: string) => {
    try {
      const response = await API.get<AffordabilityRunRecord[]>(`/btl-applications/${applicationId}/affordability-runs`);
      return response.data;
    } catch (err: any) {
      throw new Error(err.response?.data?.message ?? err);
    }
  },
  runAffordabilityCheck: async (applicationId: string) => {
    try {
      const response = await API.post<BtlAffordabilityRunResponse>(
        `/btl-applications/${applicationId}/affordability-runs/run`,
      );
      return response.data;
    } catch (err: any) {
      throw new Error(err.response?.data?.message ?? err);
    }
  },
  toggleTopSlicing: async (applicationId: string, value: boolean) => {
    try {
      const response = await API.patch<void>(`/btl-applications/${applicationId}/is-top-slicing`, {
        isTopSlicing: value,
      });
      return response.data;
    } catch (err: any) {
      throw new Error(err.response?.data?.message ?? err);
    }
  },
  getHouseholds: async (applicationId: string) => {
    try {
      const response = await API.get<Household[]>(`/btl-applications/${applicationId}/households`);
      return response.data;
    } catch (err: any) {
      throw new Error(err.response?.data?.message ?? err);
    }
  },
  createHousehold: async (applicationId: string, body: CreateHouseholdDto) => {
    try {
      const response = await API.post<Household>(
        `/btl-applications/${applicationId}/households`,
        cleanAntdFormPayload(body),
      );
      return response.data;
    } catch (err: any) {
      throw new Error(err.response?.data?.message ?? err);
    }
  },
  editHousehold: async (applicationId: string, householdId: string, body: CreateHouseholdDto) => {
    try {
      const response = await API.patch<Household>(
        `/btl-applications/${applicationId}/households/${householdId}`,
        cleanAntdFormPayload(body),
      );
      return response.data;
    } catch (err: any) {
      throw new Error(err.response?.data?.message ?? err);
    }
  },
  deleteHousehold: async (applicationId: string, householdId: string) => {
    try {
      const response = await API.delete<void>(`/btl-applications/${applicationId}/households/${householdId}`);
      return response.data;
    } catch (err: any) {
      throw new Error(err.response?.data?.message ?? err);
    }
  },
  submitApplicationFull: async (applicationId: string, body: SubmitFullApplicationDto) => {
    try {
      const response = await API.post<BtlCaseSubmissionResult>(`/btl-applications/${applicationId}/submit-full`, body);
      return response.data;
    } catch (err: any) {
      throw new Error(err.response?.data?.message ?? err);
    }
  },
  getHouseholdIndividuals: async (applicationId: string, householdId: string) => {
    try {
      const response = await API.get<Individual[]>(
        `/btl-applications/${applicationId}/households/${householdId}/individuals`,
      );
      return response.data;
    } catch (err: any) {
      throw new Error(err.response?.data?.message ?? err);
    }
  },
  createSolicitor: async (applicationId: string, body: CreateApplicationToSolicitorDto) => {
    try {
      const response = await API.post<ApplicationToSolicitor>(`/btl-applications/${applicationId}/solicitors`, body);
      return response.data;
    } catch (err: any) {
      throw new Error(err.response?.data?.message ?? err);
    }
  },
  linkSolicitor: async (applicationId: string, body: LinkApplicationToSolicitorDto) => {
    try {
      const response = await API.post<void>(`/btl-applications/${applicationId}/solicitors/link`, body);
      return response.data;
    } catch (err: any) {
      throw new Error(err.response?.data?.message ?? err);
    }
  },
  getPanelMembers: async () => {
    try {
      const response = await API.get<SolicitorFirmWithSolicitors[]>(`/monolith/solicitors/panel-members`);
      return response.data;
    } catch (err: any) {
      throw new Error(err.response?.data?.message ?? err);
    }
  },
  searchSolicitorsByRegisteredName: async (registeredName: string) => {
    try {
      const response = await API.get<SolicitorFirmWithSolicitors[]>(
        `/monolith/solicitors/search?registeredName=${encodeURIComponent(registeredName)}`,
      );
      return response.data;
    } catch (err: any) {
      throw new Error(err.response?.data?.message ?? err);
    }
  },
  getSolicitorFirmById: async (solicitorFirmId: string) => {
    try {
      const response = await API.get<SolicitorFirmWithSolicitors>(`/monolith/solicitors/${solicitorFirmId}`);
      return response.data;
    } catch (err: any) {
      throw new Error(err.response?.data?.message ?? err);
    }
  },
  updateBankAccount: async (applicationId: string, body: UpdateBankAccountDto) => {
    try {
      const response = await API.patch<void>(
        `/btl-applications/${applicationId}/bank-account`,
        cleanAntdFormPayload(body),
      );
      return response.data;
    } catch (err: any) {
      throw new Error(err.response?.data?.message ?? err);
    }
  },
  getConveyancing: async (applicationId: string) => {
    try {
      const response = await API.get<Conveyancing>(`/btl-applications/${applicationId}/conveyancing`);
      return response.data;
    } catch (err: any) {
      if (err.response?.status === 404) {
        return null;
      }

      throw new Error(err.response?.data?.message ?? err);
    }
  },
};
